import { memo, type PointerEventHandler } from "react";
import type { I18nKey } from "../../i18n";
import { PolicyControlTooltip } from "../PolicyControlTooltip";
import { Spinner } from "../ui/spinner";
import { useTranslation } from "react-i18next";
import { ComposerVoiceButton, type ComposerVoiceInputControls } from "./ComposerVoiceButton";

interface ComposerPrimaryActionsProps {
  isRunning: boolean;
  isSendBusy: boolean;
  isConnecting: boolean;
  isEnvironmentUnavailable: boolean;
  isUploading: boolean;
  hasSendableContent: boolean;
  sendDisabled?: boolean;
  sendDisabledReason?: I18nKey | null;
  voiceInput?: ComposerVoiceInputControls | null;
  preserveComposerFocusOnPointerDown?: boolean;
  onInterrupt: () => void;
}

const preventPointerFocus: PointerEventHandler<HTMLElement> = (event) => {
  event.preventDefault();
};

export const ComposerPrimaryActions = memo(function ComposerPrimaryActions({
  isRunning,
  isSendBusy,
  isConnecting,
  isEnvironmentUnavailable,
  isUploading,
  hasSendableContent,
  sendDisabled = false,
  sendDisabledReason = null,
  voiceInput = null,
  preserveComposerFocusOnPointerDown = false,
  onInterrupt,
}: ComposerPrimaryActionsProps) {
  const { t } = useTranslation();
  const pointerFocusProps = preserveComposerFocusOnPointerDown
    ? { onPointerDown: preventPointerFocus }
    : undefined;
  const voiceRecording = voiceInput?.status === "recording";
  const voiceButton =
    voiceInput && (voiceRecording || !voiceInput.hasPendingClips) ? (
      <ComposerVoiceButton
        {...voiceInput}
        preserveComposerFocusOnPointerDown={preserveComposerFocusOnPointerDown}
      />
    ) : null;

  if (isUploading || (isRunning && (!hasSendableContent || sendDisabled))) {
    return (
      <>
        {voiceButton}
        <button
          type="button"
          className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-destructive/90 text-white shadow-xs shadow-destructive/24 inset-shadow-[0_1px_--theme(--color-white/16%)] transition-all duration-150 hover:bg-destructive hover:scale-105 active:inset-shadow-[0_1px_--theme(--color-black/8%)] active:shadow-none sm:h-8 sm:w-8"
          {...pointerFocusProps}
          onClick={onInterrupt}
          aria-label={isUploading ? t("chat/stopUpload_2wct7m") : t("chat/stopGeneration_ux7kfa")}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
            <rect x="2" y="2" width="8" height="8" rx="1.5" />
          </svg>
        </button>
      </>
    );
  }

  const voiceBusy = voiceInput?.status === "finishing" || voiceInput?.status === "requesting";
  const hasVoiceContent = voiceRecording || voiceInput?.hasPendingClips === true;
  return (
    <>
      {voiceButton}
      <PolicyControlTooltip reason={sendDisabledReason}>
        <button
          type="submit"
          className="flex h-9 w-9 enabled:cursor-pointer items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-xs enabled:shadow-primary/24 enabled:inset-shadow-[0_1px_--theme(--color-white/16%)] transition-all duration-150 hover:bg-primary hover:scale-105 active:inset-shadow-[0_1px_--theme(--color-black/8%)] active:shadow-none disabled:pointer-events-none disabled:opacity-30 disabled:shadow-none disabled:hover:scale-100 sm:h-8 sm:w-8"
          {...pointerFocusProps}
          disabled={
            sendDisabled ||
            isSendBusy ||
            isConnecting ||
            isEnvironmentUnavailable ||
            voiceBusy ||
            (!hasSendableContent && !hasVoiceContent)
          }
          aria-label={
            isEnvironmentUnavailable
              ? t("chat/environmentDisconnected_r1nevz")
              : isConnecting
                ? t("common/connecting_blla0m")
                : isSendBusy
                  ? t("chat/sending_zojagc")
                  : t("chat/sendMessage_alwr73")
          }
        >
          {isConnecting || isSendBusy ? (
            <Spinner className="size-3.5" aria-hidden="true" />
          ) : (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M7 11.5V2.5M7 2.5L3 6.5M7 2.5L11 6.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      </PolicyControlTooltip>
    </>
  );
});
